import { ReceivableRow, ReceivablesPage } from '@bariendo/lib/receivables';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchBotRead } from '@/services/botCalls';

export interface ReceivablesState {
  receivables: ReceivableRow[];
  toInvoiceCount: number;
  hasMore: boolean;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/** Every receivable, once for the page: the tab's count, list and summary all read it (BAR-3038). */
export function useReceivables(): ReceivablesState {
  const medplum = useMedplum();
  const [page, setPage] = useState<ReceivablesPage>({
    receivables: [],
    hasMore: false,
    nextCursor: null,
    toInvoiceCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBotRead<{ page: ReceivablesPage }>(medplum, 'create-treatment-payment', {
      operation: 'receivables',
      receivableFilters: { status: 'all' },
    })
      .then(({ page: next }) => {
        if (!cancelled) {
          setPage(next);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // Lines from before the failure would read as current (BAR-3125).
          setPage({ receivables: [], hasMore: false, nextCursor: null, toInvoiceCount: 0 });
          setError(err instanceof Error ? err.message : 'Could not load receivables');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, reloadKey]);

  return { ...page, loading, error, reload };
}
